<template>
  <div class="chart-container">
    <div class="chart-actions">
      <DsButton icon="download" @click="downloadChart" size="small" />
      <DsButton icon="file-spreadsheet" @click="downloadCSV" size="small" />
    </div>
    <canvas width="400" height="200">
    </canvas>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { Chart, ChartData, ChartOptions, CoreScaleOptions, Scale } from 'chart.js'
  import { ChartData as CustomChartData } from '../analytics-types'
  import { transformNumberIntoFormattedCurrencyString } from '@/util/currency'
  import chartDownloadMixin from '../mixins/chartDownloadMixin'

  export default defineComponent({
    name: 'BarChart',
    mixins: [chartDownloadMixin],
    props: {
      data: {
        type: Object as () => CustomChartData,
        required: true
      },
      downloadFileName: {
        type: String,
        default: 'chart'
      },
      yStepSize: {
        type: Number,
        default: 0
      },
      moneyFormatted: {
        type: Boolean,
        default: false
      },
      stacked: {
        type: Boolean,
        default: false
      },
      showValueCounts: {
        type: Boolean,
        default: false
      },
      valueCountsLabel: {
        type: String,
        default: 'items'
      }
    },
    data() {
      return {
        chart: null as Chart | null
      }
    },
    computed: {
      computedData (): ChartData<'bar'> | null {
        if (!this.data) {
          return null
        }

        return {
          labels: this.data.labels,
          datasets: this.data.datasets
        }
      },
      options (): ChartOptions<'bar'> {
        return {
          layout: {
            padding: {
              top: 30,
              bottom: 10,
              left: 10,
              right: 10,
            },
          },
          legend: {
            display: false,
          },
          scales: {
            x: {
              grid: {
                display: false
              },
              ticks: {
                color: 'lightgrey'
              },
              stacked: this.stacked
            },
            y: {
              grid: {
                drawBorder: false,
              },
              stacked: this.stacked,
              type: this.moneyFormatted ? 'logarithmic' : 'linear',
              ticks: {
                stepSize: this.yStepSize,
                color: 'lightgrey',
                padding: 10,
                font: {
                  size: 11
                },
                callback: function(this: Scale<CoreScaleOptions>, tickValue: number | string) {
                  const value = Number(tickValue)
                  if (this.moneyFormatted) {
                    return transformNumberIntoFormattedCurrencyString(value)
                  }
                  return value
                },
                beginAtZero: true
              }
            }
          },
          plugins: {
            tooltip: {
              backgroundColor: 'var(--primary-lighter)',
              callbacks: {
                title: (context) => {
                  return context[0].label
                },
                label: (context) => {
                  const value = context.parsed.y
                  const label = context.dataset.label
                  const valueCount = context.dataset.valueCounts?.[context.dataIndex]

                  if (this.moneyFormatted) {
                    if (this.showValueCounts && valueCount) {
                      return `${transformNumberIntoFormattedCurrencyString(value)} across ${valueCount} ${this.valueCountsLabel}`
                    }
                    return transformNumberIntoFormattedCurrencyString(value)
                  }

                  if (this.showValueCounts && valueCount) {
                    return `${value} across ${valueCount} ${this.valueCountsLabel}`
                  }
                  return `${label}: ${value}`
                }
              }
            }
          }
        }
      },
      hasData(): boolean {
        if (!this.data?.datasets?.length) return false;
        return this.data.datasets.some(dataset => dataset.data.some(value => value > 0));
      }
    },
    methods: {
      // Remove downloadChart and downloadCSV methods as they are now in the mixin
    },
    mounted () {
      if (this.computedData) {
        this.chart = new Chart(this.$el.querySelector('canvas') as HTMLCanvasElement, {
          type: 'bar',
          data: this.computedData,
          options: this.options
        } as any)
      }
    },
    watch: {
      data (data: CustomChartData) {
        if (this.chart) {
          this.chart.destroy()
        }

        if (this.computedData) {
          this.chart = new Chart(this.$el.querySelector('canvas') as HTMLCanvasElement, {
            type: 'bar',
            data: this.computedData,
            options: this.options
          } as any)
        }
      }
    }
  })
</script>

<style lang="scss" scoped>
.chart-container {
  position: relative;

  .chart-actions {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1;
    display: flex;
    gap: 2px;
  }
}

canvas {
  background-color: #f7f7f7;
}
</style>
